import React, { useState } from 'react';
import { Wrench, RefreshCw, ExternalLink } from 'lucide-react';
import { pingHealth, reportHealthy, HEALTH_URL, API_HOST } from '../config/maintenance';

// Full-screen "we'll be right back" page shown when the backend is unreachable.
// The app polls /health in the background and recovers automatically; this button
// just lets an impatient user force an immediate re-check.
//
// The verdict is made by THIS browser (it could not reach the API host), so
// when colleagues are fine the cause is almost always local — a network that
// blocks the API host, a VPN, an ad-blocker / security extension, or an
// antivirus web shield. The screen says so and offers a one-click self-test.
export default function MaintenanceScreen() {
  const [checking, setChecking] = useState(false);
  const [failedChecks, setFailedChecks] = useState(0);
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;

  const checkNow = async () => {
    setChecking(true);
    const ok = await pingHealth();
    setChecking(false);
    if (ok) {
      reportHealthy();
      window.location.reload();
    } else {
      setFailedChecks((n) => n + 1);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-100 px-4">
      <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-10 max-w-md w-full text-center">
        <div className="mx-auto mb-6 h-16 w-16 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
          <Wrench className="h-8 w-8 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-3">We&rsquo;ll be right back</h1>
        <p className="text-gray-500 leading-relaxed mb-6">
          {offline
            ? 'Your device appears to be offline. Reconnect and the page will carry on automatically.'
            : <>This browser can&rsquo;t reach AcuityFlo&rsquo;s server right now. Your data is safe &mdash; the page will reconnect automatically as soon as it can.</>}
        </p>
        <button
          onClick={checkNow}
          disabled={checking}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 disabled:opacity-60 transition-colors"
        >
          <RefreshCw className={'h-4 w-4 ' + (checking ? 'animate-spin' : '')} />
          {checking ? 'Checking…' : 'Check again'}
        </button>
        {failedChecks > 0 && !offline && (
          <div className="mt-6 text-left text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
            <p className="font-medium text-gray-700">Still can&rsquo;t connect? If colleagues can log in, the block is on this device or network:</p>
            <ol className="list-decimal pl-4 space-y-1">
              <li>
                Open <a href={HEALTH_URL} target="_blank" rel="noopener noreferrer" className="text-orange-600 underline inline-flex items-center gap-0.5">{API_HOST}/health <ExternalLink className="h-3 w-3" /></a> in a new tab. It should show a short line of text. If it doesn&rsquo;t load, your network, VPN or antivirus is blocking <span className="font-mono">{API_HOST}</span> &mdash; try a different network (e.g. a phone hotspot) or ask IT to allow that address.
              </li>
              <li>Try a private/incognito window, which disables browser extensions (ad-blockers and privacy tools sometimes block it).</li>
              <li>If it works on the hotspot but not the office network, send IT the address above.</li>
            </ol>
          </div>
        )}
        <p className="text-xs text-gray-400 mt-6">Acuity Professional</p>
      </div>
    </div>
  );
}
